// Однолінійна схема станції: точка приєднання → шина 10 кВ → КТП → групи
// інверторів, плюс нижня панель деталей вибраного вузла.
//
// ПРОДУКТИВНІСТЬ. Уся геометрія — відсоткові координати в SVG (див.
// SchemeLines), тож при монтуванні чи перемиканні вкладки не виконується
// жодного вимірювання DOM і не заводиться ResizeObserver. Модель рахується
// один раз у useMemo від телеметрії. Анімація потоку — CSS на SVG-шляхах.
//
// АДАПТИВНІСТЬ. КТП стоять у CSS-сітці, а лінії прив'язані до частки ширини,
// а не до пікселів, тож перебудовуються самі. На вузьких екранах шина
// ховається: коли картки переносяться в кілька рядів, одна горизонталь уже
// нічого не з'єднує коректно, і чесніше показати просто список секцій.
import { cx } from '@/i18n/cfgText';
import i18n from '@/i18n';
import { useMemo, useState } from 'react';
import { scada, stateColor, STATE_LABEL } from '@/scada/tokens';
import type { StationTelemetry } from '@/scada/mock/telemetry';
import type { StationDetail } from '@/scada/mock/stationDetail';
import { buildSchemeModel } from './mockData';
import { BusBand, KtpToGroupsBand } from './SchemeLines';
import ConnectionPoint from './ConnectionPoint';
import KtpNode from './KtpNode';
import InverterGroup from './InverterGroup';
import BessNode from './BessNode';
import type { SchemeSelection, SchemeState, SchemeLegend } from './types';

const LEGEND_ORDER: Array<{ key: keyof SchemeLegend; state: SchemeState }> = [
  { key: 'ok', state: 'ok' },
  { key: 'warning', state: 'warning' },
  { key: 'alarm', state: 'alarm' },
  { key: 'offline', state: 'offline' },
];

/** Одна клітинка нижньої панелі. */
function DetailCell({ label, value, unit }: { label: string; value: string; unit?: string }) {
  return (
    <div style={{
      background: scada.panel2, border: `1px solid ${scada.border}`,
      borderRadius: 8, padding: '8px 10px', minWidth: 0,
    }}>
      <div style={{ fontSize: 10, color: scada.textMuted, marginBottom: 3, whiteSpace: 'nowrap' }}>{label}</div>
      <div className="flex items-baseline" style={{ gap: 4 }}>
        <span className="font-display tabular-nums" style={{
          fontSize: 16, fontWeight: 600, lineHeight: 1, color: scada.text,
        }}>{value}</span>
        {unit && <span style={{ fontSize: 10, color: scada.textMuted }}>{unit}</span>}
      </div>
    </div>
  );
}

export default function StationScheme({ t, detail }: {
  t: StationTelemetry;
  detail: StationDetail;
}) {
  const model = useMemo(() => buildSchemeModel(t, detail), [t, detail]);
  const [selection, setSelection] = useState<SchemeSelection>({ kind: 'none' });

  const selectedKtpId = selection.kind === 'ktp' || selection.kind === 'inverter' ? selection.ktpId : null;

  // УЗЕ приєднане до тієї самої шини, що й КТП, тож на схемі це ще одна
  // колонка в тому ж ряду — просто без груп інверторів під нею. Через це
  // всі розрахунки шини ведуться по СПИСКУ ВУЗЛІВ, а не по ktps.
  const busStates = [...model.ktps.map((k) => k.state), ...(model.bess ? [model.bess.state] : [])];
  const cols = busStates.length;
  const activeBusIndex = selection.kind === 'bess'
    ? cols - 1
    : selectedKtpId
      ? model.ktps.findIndex((k) => k.id === selectedKtpId)
      : null;

  // Дані для нижньої панелі: або вибраний інвертор, або вибрана КТП.
  const detailView = useMemo(() => {
    if (selection.kind === 'inverter') {
      const ktp = model.ktps.find((k) => k.id === selection.ktpId);
      const group = ktp?.groups.find((g) => g.id === selection.groupId);
      const inv = group?.inverters.find((x) => x.id === selection.inverterId);
      if (ktp && group && inv) {
        return {
          title: i18n.t('ak_scada2_inverter_2', { id: inv.id }),
          subtitle: `${cx(ktp.name)} • ${group.label}`,
          state: inv.state,
          cells: [
            { label: i18n.t('ak_scada2_power'), value: String(Math.round(inv.powerKw)), unit: i18n.t('ak_scada2_kw') },
            { label: i18n.t('ak_scada2_voltage'), value: String(inv.voltageKv), unit: i18n.t('ak_scada2_kv') },
            { label: i18n.t('ak_scada2_current'), value: String(inv.currentA), unit: 'A' },
            { label: i18n.t('ak_scada2_temperature'), value: inv.tempC ? String(inv.tempC) : '—', unit: inv.tempC ? '°C' : undefined },
            { label: i18n.t('ak_scada2_efficiency'), value: inv.efficiencyPct != null ? inv.efficiencyPct.toFixed(1) : '—', unit: inv.efficiencyPct != null ? '%' : undefined },
          ],
        };
      }
    }
    if (selection.kind === 'bess' && model.bess) {
      const b = model.bess;
      const charging = b.mode === 'charging';
      return {
        title: i18n.t('ak_scada2_bess_2'),
        subtitle: i18n.t('ak_scada2_storage', { m: b.modeLabel }),
        state: b.state,
        cells: [
          { label: i18n.t('ak_scada2_charge'), value: String(b.socPct), unit: '%' },
          // Знак у телеметрії: + розряд, − заряд. На панелі показуємо модуль
          // і підписуємо напрям словом — так менше шансів прочитати навпаки.
          { label: charging ? i18n.t('ak_scada2_charging_power') : i18n.t('ak_scada2_discharging_power'),
            value: String(Math.abs(Math.round(b.powerKw))), unit: i18n.t('ak_scada2_kw') },
          { label: i18n.t('ak_scada2_mode'), value: b.modeLabel },
          { label: i18n.t('ak_scada2_health'), value: b.sohPct != null ? String(b.sohPct) : '—', unit: b.sohPct != null ? '%' : undefined },
        ],
      };
    }
    if (selection.kind === 'ktp') {
      const ktp = model.ktps.find((k) => k.id === selection.ktpId);
      if (ktp) {
        return {
          title: cx(ktp.name),
          subtitle: i18n.t('ak_scada2_inverters_running_2', { w: ktp.working, t: ktp.total }),
          state: ktp.state,
          cells: [
            { label: i18n.t('ak_scada2_power'), value: String(Math.round(ktp.powerKw)), unit: i18n.t('ak_scada2_kw') },
            { label: i18n.t('ak_scada2_voltage'), value: String(ktp.voltageKv), unit: i18n.t('ak_scada2_kv') },
            { label: i18n.t('ak_scada2_current'), value: String(ktp.currentA), unit: 'A' },
            { label: i18n.t('ak_scada2_temperature'), value: String(ktp.tempC), unit: '°C' },
            { label: i18n.t('ak_scada2_groups'), value: String(ktp.groups.length) },
          ],
        };
      }
    }
    return null;
  }, [selection, model]);

  return (
    <div style={{ display: 'grid', gap: 10, padding: '4px 2px 8px', minWidth: 0 }}>
      {/* Легенда станів — над схемою, праворуч. */}
      <div className="flex items-start justify-end" style={{ gap: 12, flexWrap: 'wrap' }}>
        <div className="flex items-center" style={{
          gap: 10, background: scada.panel, border: `1px solid ${scada.border}`,
          borderRadius: 10, padding: '8px 12px', flexShrink: 0,
        }}>
          {LEGEND_ORDER.map(({ key, state }) => (
            <div key={key} className="flex items-center" style={{ gap: 5 }}>
              <span style={{ width: 6, height: 6, borderRadius: 999, background: stateColor(state) }} />
              <span style={{ fontSize: 10, color: scada.textMuted, whiteSpace: 'nowrap' }}>{STATE_LABEL[state]}</span>
              <span className="font-display tabular-nums" style={{ fontSize: 12, fontWeight: 600, color: scada.text }}>
                {model.legend[key]}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Шина й колонки КТП живуть в ОДНОМУ прокрутному контейнері з
          однаковою сіткою. Це принципово: шина малюється у відсотках ширини,
          тож вона збігається з колонками лише доти, доки їх рівно cols в один
          ряд. Якби сітка переносила картки на другий ряд (auto-fit або
          Tailwind-брейкпоінти), горизонталь показувала б неіснуючі зв'язки.
          Тому на вузькому екрані — горизонтальна прокрутка, а не перенос. */}
      <div style={{ overflowX: 'auto', overflowY: 'hidden', minWidth: 0 }}>
        {/* gap: 0 по всій вертикалі схеми — провідники мусять торкатися
            вузлів. Будь-який проміжок тут читається як обрив лінії. */}
        <div style={{ minWidth: cols * 150, display: 'grid', gap: 0 }}>
          {/* Точка приєднання + шина — ОДИН блок без проміжку (gap: 0).
              Картка стоїть усередині прокрутного контейнера, бо ввід шини
              малюється на 50% ширини цього ж блоку: винесена назовні, вона
              центрувалась би у вужчій шапці, і лінія входила б повз неї.
              А спільний gap батьківської сітки лишав би розрив між нижньою
              гранню картки й початком провідника. */}
          <div style={{ display: 'grid', gap: 0 }}>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <ConnectionPoint data={model.connection} />
            </div>
            <BusBand count={cols} states={busStates} activeIndex={activeBusIndex} />
          </div>

          <div style={{
            display: 'grid', gap: 10, minWidth: 0,
            gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
          }}>
        {model.ktps.map((k) => (
          <div key={k.id} style={{ display: 'grid', gap: 0, minWidth: 0, alignContent: 'start' }}>
            <KtpNode
              ktp={k}
              selected={selectedKtpId === k.id}
              onSelect={() => setSelection(
                selection.kind === 'ktp' && selection.ktpId === k.id
                  ? { kind: 'none' }
                  : { kind: 'ktp', ktpId: k.id },
              )}
            />
            <KtpToGroupsBand
              groupStates={k.groups.map((g) => g.state)}
              active={selectedKtpId === k.id}
            />
            <div style={{
              display: 'grid', gap: 6, minWidth: 0,
              gridTemplateColumns: `repeat(${k.groups.length}, minmax(0, 1fr))`,
            }}>
              {k.groups.map((g) => (
                <InverterGroup
                  key={g.id}
                  group={g}
                  selection={selection}
                  onSelectInverter={(inverterId) => setSelection({
                    kind: 'inverter', ktpId: k.id, groupId: g.id, inverterId,
                  })}
                />
              ))}
            </div>
          </div>
        ))}

        {/* Колонка УЗЕ — остання на шині. Груп інверторів під нею немає,
            тож розводка вниз не малюється: накопичувач вмикається в шину
            напряму. */}
        {model.bess && (
          <div style={{ display: 'grid', gap: 0, minWidth: 0, alignContent: 'start' }}>
            <BessNode
              bess={model.bess}
              selected={selection.kind === 'bess'}
              onSelect={() => setSelection(
                selection.kind === 'bess' ? { kind: 'none' } : { kind: 'bess' },
              )}
            />
          </div>
        )}
          </div>
        </div>
      </div>

      {/* Нижня панель — тільки коли щось вибрано: порожня панель із
          прочерками займала б місце й нічого не повідомляла. */}
      {detailView && (
        <div style={{
          background: scada.panel, border: `1px solid ${scada.border}`,
          borderRadius: 11, padding: '11px 13px', display: 'grid', gap: 10,
          boxShadow: 'var(--shadow)',
        }}>
          <div className="flex items-center justify-between" style={{ gap: 10 }}>
            <div className="flex items-center" style={{ gap: 9, minWidth: 0 }}>
              <span style={{
                width: 8, height: 8, borderRadius: 999,
                background: stateColor(detailView.state), flexShrink: 0,
              }} />
              <div style={{ minWidth: 0 }}>
                <div className="font-display" style={{ fontSize: 13, fontWeight: 700, color: scada.text }}>
                  {detailView.title}
                </div>
                <div style={{ fontSize: 10.5, color: scada.textMuted }}>{detailView.subtitle}</div>
              </div>
              <span className="font-display" style={{
                fontSize: 10, fontWeight: 600, letterSpacing: '.05em', textTransform: 'uppercase',
                color: stateColor(detailView.state), marginLeft: 4, whiteSpace: 'nowrap',
              }}>{STATE_LABEL[detailView.state]}</span>
            </div>
            <button
              type="button"
              onClick={() => setSelection({ kind: 'none' })}
              aria-label={i18n.t('ak_scada2_close')}
              style={{
                background: 'transparent', border: 'none', cursor: 'pointer',
                color: scada.textMuted, fontSize: 16, lineHeight: 1, padding: 2, flexShrink: 0,
              }}
            >
              <i className="ri-close-line" />
            </button>
          </div>

          <div style={{
            display: 'grid', gap: 7,
            gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))',
          }}>
            {detailView.cells.map((c) => (
              <DetailCell key={c.label} label={c.label} value={c.value} unit={c.unit} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
